/**
 * Owned nodes → what a run actually starts with (doc 08).
 *
 * Two audiences: `effectsOf` answers the UI (what is unlocked, which overlays
 * are on), `modifiersOf` answers the sim (the numbers a run is built with).
 * Anything gated is gated *by construction*: the base roster is every placeable
 * the tree does not grant, so adding a node to the JSON gates its building.
 */

import { PLACEABLE } from '../sim/defs.ts';
import type { RunModifiers } from '../sim/types.ts';
import { ASSIST, NODES } from './tree.ts';
import type { MetaEffects, Profile } from './types.ts';
import { levelOf } from './xp.ts';

/** Defs some node hands out — everything else is yours from run one. */
const GRANTED_DEFS = new Set(
  Object.values(NODES)
    .flatMap((n) => [n.grants.building, n.grants.tower])
    .filter((d): d is string => d !== undefined),
);

export const BASE_DEFS = Object.keys(PLACEABLE).filter((d) => !GRANTED_DEFS.has(d));

export function effectsOf(owned: string[]): MetaEffects {
  const out: MetaEffects = {
    coreHpMult: 1,
    structureHpMult: 1,
    storageMult: 1,
    doubleReelBonus: null,
    kitBonus: {},
    buildings: [],
    towers: [],
    abilities: ['q'],
    shoalCounts: false,
    codex: false,
    rangeOverlay: false,
  };
  for (const id of owned) {
    const n = NODES[id];
    if (!n) continue; // a node retired between versions: ignore, never crash
    const gr = n.grants;
    if (gr.coreHpMult !== undefined) out.coreHpMult *= gr.coreHpMult;
    if (gr.structureHpMult !== undefined) out.structureHpMult *= gr.structureHpMult;
    if (gr.storageMult !== undefined) out.storageMult *= gr.storageMult;
    if (gr.doubleReelBonus !== undefined) out.doubleReelBonus = gr.doubleReelBonus;
    if (gr.kit) for (const [res, amt] of Object.entries(gr.kit)) {
      out.kitBonus[res as keyof typeof out.kitBonus] =
        (out.kitBonus[res as keyof typeof out.kitBonus] ?? 0) + amt;
    }
    if (gr.building !== undefined) out.buildings.push(gr.building);
    if (gr.tower !== undefined) out.towers.push(gr.tower);
    if (gr.ability !== undefined) out.abilities.push(gr.ability);
    if (gr.shoalCounts) out.shoalCounts = true;
    if (gr.codex) out.codex = true;
    if (gr.rangeOverlay) out.rangeOverlay = true;
  }
  return out;
}

/**
 * Doc 08 §Assist: Core +25% HP, +10% for every failed run while it was on,
 * capped at +50% total. Off = exactly 1.
 */
export function assistCoreMult(profile: Profile): number {
  if (!profile.settings.assist) return 1;
  return 1 + Math.min(ASSIST.maxBonus, ASSIST.coreHpBonus + ASSIST.perLossBonus * profile.assistLosses);
}

/** The sim's half of the meta: the numbers a run is constructed with. */
export function modifiersOf(profile: Profile, baseDoubleReel: number): RunModifiers {
  const eff = effectsOf(profile.owned);
  const towerLevels: Record<string, number> = {};
  for (const def of Object.keys(PLACEABLE)) {
    if (PLACEABLE[def]!.kind === 'tower') towerLevels[def] = levelOf(profile.towerXp[def] ?? 0);
  }
  for (const def of eff.towers) towerLevels[def] ??= levelOf(profile.towerXp[def] ?? 0);
  return {
    coreHpMult: eff.coreHpMult * assistCoreMult(profile),
    structureHpMult: eff.structureHpMult,
    storageMult: eff.storageMult,
    doubleReelBonus: eff.doubleReelBonus ?? baseDoubleReel,
    towerLevels,
    allowedDefs: [...BASE_DEFS, ...eff.buildings, ...eff.towers],
    allowedAbilities: eff.abilities,
    // Pressure is folded in on top by `applyPressure` (doc 08 §Pressure)
    enemyHpMult: 1,
    bossHpMult: 1,
    gimmickRateMult: 1,
    costMult: 1,
    sellRefundMult: 1,
    autoLureSeconds: 0,
  };
}
